/**
 * RequirePermission — the route guard.
 *
 * Wraps every element mounted from the navigation registry. If the user does
 * not hold the entry's permission, the route renders an explicit 403 screen
 * rather than the page — so a bookmarked or hand-typed URL cannot mount a
 * screen that would immediately fire requests the API will refuse.
 *
 * Again, for the record: this is UX, not security. The API re-checks every
 * request with `requirePermission(...)` plus a scoped DB query. A user who
 * patches this component out sees an empty page full of 403s.
 */
import type { ReactNode } from 'react';
import { EmptyState } from '@sonauto/ui';
import { useAuth } from './AuthProvider.js';
import { LinkButton } from '../components/LinkButton.js';

export interface RequirePermissionProps {
  /** `null` = any signed-in user. */
  readonly permission: string | null;
  readonly children: ReactNode;
}

export function RequirePermission({ permission, children }: RequirePermissionProps): React.JSX.Element {
  const { has } = useAuth();

  if (permission !== null && !has(permission)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <EmptyState
          icon="🔒"
          title="Bạn không có quyền truy cập màn hình này"
          description={
            <>
              Màn hình này yêu cầu quyền <code className="font-mono text-fg">{permission}</code>. Nếu bạn cho rằng
              đây là nhầm lẫn, hãy liên hệ quản trị viên của chuỗi để được cấp quyền.
            </>
          }
          action={<LinkButton to="/">Về trang chủ</LinkButton>}
        />
      </div>
    );
  }

  return <>{children}</>;
}
